import { Text } from "@mantine/core";
import { useEffect, useState } from "react";
import {
  EmptyState,
  ErrorState,
  ListView,
  LoadingSkeleton,
  RecordCard,
  StateBadge,
  useColumns,
} from "../../components/ui";
import { api, type RegisterEntry } from "../../lib/api";
import { formatINR } from "../../lib/money";
import { useAuth } from "../../auth/AuthContext";
import { Page } from "../../shell/Page";

/**
 * Every receipt, both kinds, newest first.
 *
 * WHY THIS SCREEN EXISTS
 *
 * There was no list of payments anywhere in this application. Not a screen,
 * not a report, not an export - bills and outstanding balances were listed,
 * and the money actually taken was write-only. That was survivable while
 * every receipt sat against a bill this system could show a balance for. It
 * stopped being survivable the moment agents could record payments against
 * bills raised somewhere else, because the register is now the ONLY place
 * those appear at all.
 *
 * WHAT SCOPES IT
 *
 * The server, not this screen. A Store Manager or Admin gets every receipt;
 * anybody else gets their own. There is no scope argument to pass and
 * therefore none to get wrong - the export endpoint reads the same scoped
 * function, so a spreadsheet cannot be the way round the list.
 *
 * The heading changes to match, because a page called "All payments" showing
 * one agent's own is a page that lies quietly.
 */
export function PaymentsRegister() {
  const [payments, setPayments] = useState<RegisterEntry[]>();
  const [error, setError] = useState<string>();
  const { hasRole } = useAuth();

  /**
   * Whether this person is seeing everything or only their own.
   *
   * A local copy of a rule the server owns, used ONLY to choose a heading and
   * a column. It must not be used to decide what to fetch - that is the
   * server's job - and the two lists in `PAYMENT_REGISTER_OVERSEERS` agreeing
   * is asserted by a test rather than by hope.
   */
  const seesEveryone = hasRole("STORE_MANAGER", "ADMIN");

  async function load() {
    try {
      setError(undefined);
      const { payments: loaded } = await api.paymentsRecorded();
      setPayments(loaded);
    } catch (caught) {
      setError(
        caught instanceof Error ? caught.message : "Could not load the register.",
      );
    }
  }

  useEffect(() => {
    void load();
  }, []);

  const total = (payments ?? []).reduce(
    (sum, payment) => sum + Number(payment.amount),
    0,
  );

  const columns = useColumns<RegisterEntry>(
    () => [
      {
        key: "Collected",
        header: "Collected",
        width: "140px",
        render: (payment) => (
          <Text fz={13} className="num" c="gray.7">
            {new Date(payment.collectedAt).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })}
          </Text>
        ),
      },
      {
        // THE column this screen exists to show. Without it the two kinds are
        // indistinguishable, and an external receipt reads as a payment
        // against a bill somebody could go and look up.
        key: "Against",
        header: "Against",
        width: "120px",
        render: (payment) => (
          <StateBadge
            state={payment.against === "BILL" ? "dlvDelivered" : "dlvPending"}
            label={payment.against === "BILL" ? "Our bill" : "Elsewhere"}
          />
        ),
      },
      {
        key: "Reference",
        header: "Reference",
        width: "160px",
        render: (payment) =>
          payment.reference ? (
            <Text fz={13} fw={600} className="num" truncate>
              {payment.reference}
            </Text>
          ) : (
            // An external receipt whose paperwork carried no number. The
            // business made that field optional, so this is a real state and
            // not a gap in the data.
            <Text fz={13} c="gray.6">
              none given
            </Text>
          ),
      },
      {
        key: "Customer",
        header: "Customer",
        width: "auto",
        render: (payment) => (
          <Text fz={14} truncate>
            {payment.customerName}
          </Text>
        ),
      },
      {
        key: "Amount",
        header: "Amount",
        width: "140px",
        align: "right",
        render: (payment) => (
          <Text fz={15} fw={700} className="num">
            {formatINR(payment.amount)}
          </Text>
        ),
      },
      {
        key: "Method",
        header: "Method",
        width: "110px",
        render: (payment) => (
          <Text fz={13} c="gray.7">
            {payment.paymentMethod === "ONLINE"
              ? "Online"
              : payment.paymentMethod.charAt(0) +
                payment.paymentMethod.slice(1).toLowerCase()}
          </Text>
        ),
      },
      {
        key: "Register",
        header: "Register",
        width: "120px",
        render: (payment) => (
          <Text fz={13} c="gray.7">
            {payment.voucherType === "CASH_RECEIPT" ? "Cash book" : "Bank book"}
          </Text>
        ),
      },
      {
        key: "Receipt no",
        header: "Receipt no",
        width: "130px",
        render: (payment) =>
          payment.manualReceiptNo ? (
            <Text fz={13} className="num" c="gray.7">
              {payment.manualReceiptNo}
            </Text>
          ) : (
            <Text fz={13} c="gray.6">
              —
            </Text>
          ),
      },
      // Only where it means something. For an agent looking at their own
      // receipts, a column repeating their own name in every row is a column
      // that costs width and says nothing.
      ...(seesEveryone
        ? [
            {
              key: "Collected by",
              header: "Collected by",
              width: "170px",
              render: (payment: RegisterEntry) => (
                <Text fz={13} c="gray.7" truncate>
                  {payment.collectedBy.name}
                </Text>
              ),
            },
          ]
        : []),
    ],
    [seesEveryone],
  );

  return (
    <Page
      title={seesEveryone ? "Payments recorded" : "Payments you recorded"}
      subtitle={
        payments
          ? `${payments.length} receipt${payments.length === 1 ? "" : "s"} · ${formatINR(
              total.toFixed(2),
            )}`
          : undefined
      }
      back="/collections"
      width="wide"
    >
      {error && <ErrorState message={error} onRetry={() => void load()} />}
      {!payments && !error && <LoadingSkeleton variant="list" count={5} />}

      {payments?.length === 0 && (
        <EmptyState
          title={seesEveryone ? "Nothing recorded yet" : "You have not recorded any"}
          description={
            seesEveryone
              ? "Receipts appear here as agents record them, against our bills and against bills raised elsewhere."
              : "Collect against an outstanding bill, or record a payment for a bill raised on another system, and it will appear here."
          }
        />
      )}

      {payments && payments.length > 0 && (
        <ListView
          rows={payments}
          columns={columns}
          rowKey={(payment) => payment.id}
          // No onOpen. There is no payment detail screen, and a row that looks
          // clickable and does nothing is worse than one that does not.
          exportDataset="payments"
          caption="Payments recorded"
          footnote={
            seesEveryone
              ? "Both kinds. A receipt marked Elsewhere was collected against a bill another system raised, so this application holds no balance for it — the amount stands on the photo, location and signature captured with it."
              : "Your own receipts. A receipt marked Elsewhere was collected against a bill another system raised."
          }
          renderCard={(payment) => (
            <RecordCard
              key={payment.id}
              primary={payment.customerName}
              secondary={
                payment.reference
                  ? `${payment.against === "BILL" ? "Our bill" : "Their bill"} ${payment.reference}`
                  : "Bill raised elsewhere, no number given"
              }
              action={
                <span className="num" style={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                  {formatINR(payment.amount)}
                </span>
              }
              meta={`${new Date(payment.collectedAt).toLocaleDateString()} · ${
                payment.voucherType === "CASH_RECEIPT" ? "cash book" : "bank book"
              }${payment.manualReceiptNo ? ` · receipt ${payment.manualReceiptNo}` : ""}${
                seesEveryone ? ` · ${payment.collectedBy.name}` : ""
              }`}
            />
          )}
        />
      )}
    </Page>
  );
}
